import { ref, computed, toValue } from "vue";
import { parseUid } from "../utils/uid-generator";

/**
 * Composable for sorting lists while keeping the currently focused item pinned in place.
 * Prevents jarring repositioning while the user is actively editing a card.
 *
 * @param {Object} options
 * @param {Function|Ref} options.items - Reactive items array or getter
 * @param {Function} options.sortFn - Function that takes items array and returns sorted array
 * @param {string} [options.uidKey='_uid'] - Property name to use as UID on items
 * @param {string} [options.dataAttr='data-uid'] - Data attribute name to look for in DOM
 */
export function useFocusAwareSort(options = {}) {
  const { items, sortFn, uidKey = "_uid", dataAttr = "data-uid" } = options;

  const focusedUid = ref(null);
  // The visual index where the focused item was when focus started
  const pinnedIndex = ref(null);

  // Convert data attribute name to selector and dataset key
  // e.g., "data-blend-uid" -> "[data-blend-uid]" and "blendUid"
  const selector = `[${dataAttr}]`;
  const datasetKey = dataAttr.replace(/^data-/, "").replace(/-([a-z])/g, (_, c) => c.toUpperCase());

  /**
   * Handle focusin events on the container.
   * Sets the focused UID and captures its current visual position.
   */
  function handleFocusIn(event) {
    const itemEl = event.target.closest(selector);
    if (!itemEl) return;

    const uid = parseUid(itemEl.dataset[datasetKey]);
    if (uid === undefined || uid === null) return;

    // Only capture position on initial focus (not when moving within same card)
    if (focusedUid.value === uid) return;

    // Capture current sorted position before setting focus
    const list = toValue(items);
    const sorted = sortFn(list);
    const currentIndex = sorted.findIndex((item) => item[uidKey] === uid);

    focusedUid.value = uid;
    pinnedIndex.value = currentIndex >= 0 ? currentIndex : null;
  }

  /**
   * Handle focusout events on the container.
   * Clears the focused UID if focus moves outside the container.
   */
  function handleFocusOut(event) {
    // relatedTarget is the element receiving focus
    // If it's still within our container, don't clear
    const container = event.currentTarget;
    if (event.relatedTarget && container.contains(event.relatedTarget)) {
      return;
    }
    focusedUid.value = null;
    pinnedIndex.value = null;
  }

  /**
   * Computed sorted list that pins the focused item at its captured position.
   */
  const sortedItems = computed(() => {
    const list = toValue(items);
    if (!list || list.length === 0) return [];

    // Apply the sort function
    const sorted = sortFn(list);

    // If nothing is focused or no pinned position, just return sorted list
    if (focusedUid.value === null || pinnedIndex.value === null) {
      return sorted;
    }

    // Find the focused item in the newly sorted list
    const currentSortedIndex = sorted.findIndex((item) => item[uidKey] === focusedUid.value);
    if (currentSortedIndex === -1) {
      // Focused item no longer exists
      return sorted;
    }

    // If already at pinned position, no adjustment needed
    if (currentSortedIndex === pinnedIndex.value) {
      return sorted;
    }

    // Move the focused item from its current sorted position to the pinned position
    const result = [...sorted];
    const [focusedItem] = result.splice(currentSortedIndex, 1);

    // Clamp pinned index to valid range (in case items were removed)
    const targetIndex = Math.min(pinnedIndex.value, result.length);
    result.splice(targetIndex, 0, focusedItem);

    return result;
  });

  return {
    focusedUid,
    sortedItems,
    handleFocusIn,
    handleFocusOut,
  };
}
